import React, {useState, useEffect} from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import _ from 'lodash';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {v4 as uuidv4} from 'uuid';
import BobModal from './BobModal';
import classes from './FormulaGridColumnModal.module.css';

const operators = [
    {operator: '+', display: 'Plus'},
    {operator: '-', display: 'Minus'},
    {operator: '*', display: 'Times'},
    {operator: '/', display: 'Divide'}
];

const ValidationSchema = Yup.object().shape({
    columnOne: Yup.string().required('Required').typeError('Select column'),
    columnTwo: Yup.string().required('Required').typeError('Select column'),
    operator:  Yup.string().required('Required').typeError('Select operator'),
    cellTitle: Yup.string().required('Column title is required').typeError('Enter column title')
});

const FormulaGridColumnModal = ({
    cellIndex,
    columnList,
    formula,
    setFormulaValues,
    setFormulaFormIsOpen,
    title,
    ...props
}) => {
    const [column1Fields, setColumn1Fields] = useState([]);
    const [column2Fields, setColumn2Fields] = useState([]);
    const [fieldName, setFieldName] = useState();

    useEffect(() => {
        if (!formula) {
            const fieldName = uuidv4();
            setFieldName(fieldName);
        }
        else {
            setFieldName(formula.fieldName);
        }
    }, [formula]);

    useEffect(() => {
        setColumn1Fields(_.cloneDeep(columnList).filter(c => c.filter === 'numeric' && c.field !== formula?.fieldName));
        setColumn2Fields(_.cloneDeep(columnList).filter(c => c.filter === 'numeric' && c.field !== formula?.fieldName));
    }, [columnList]);

    const formik = useFormik({
        initialValues: {
            columnOne: formula ? formula.columnOne : null,
            columnTwo: formula ? formula.columnTwo : null,
            operator:  formula ? formula.operator : null,
            cellTitle: formula ? formula.cellTitle : '',
            precision: formula ? formula.precision : 0,
            fieldName: fieldName,
            cellIndex: formula ? formula.cellIndex : cellIndex
        },
        enableReinitialize: true,
        validationSchema:   ValidationSchema,
        onSubmit:           async (values, {setSubmitting}) => {
            setFormulaValues(values);
            setFormulaFormIsOpen(false);
        }
    });

    const handleOptionChange = (event, values, field) => {
        formik.setFieldValue(field, values, true);
    };

    return (
        <BobModal
            confirmText={formula ? 'Update' : 'Create'}
            cancelText="Cancel"
            onCancel={() => {setFormulaFormIsOpen(false);}}
            onClose={() => {setFormulaFormIsOpen(false);}}
            onConfirm={formik.handleSubmit}
            title={title}
        >
            <React.Fragment>
                <div className={classes.CellTitle}>
                    <TextField
                        id="cellTitle"
                        name="cellTitle"
                        variant={'outlined'}
                        size="small"
                        label={'Column Header *'}
                        value={formik.values.cellTitle}
                        onChange={formik.handleChange}
                        error={Boolean(formik.errors.cellTitle)}
                        helperText={Boolean(formik.touched.cellTitle) && formik.errors.cellTitle}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                root: classes.TextField
                            }
                        }}
                    />
                </div>
                <div className={classes.FormulaRow}>
                    <div className={classes.columnOne}>
                        <FormControl className={formik.errors.columnOne ? classes.ErrorWrapper : classes.InputWrapper}>
                            <Autocomplete
                                id="columnOne"
                                name="columnOne"
                                autoComplete
                                options={column1Fields}
                                getOptionLabel={(option) => option.title}
                                getOptionSelected={(option, value) => option.field === value.field}
                                onChange={(event, values) => handleOptionChange(event, values, 'columnOne')}
                                onBlur={formik.handleBlur}
                                size="small"
                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        label="Start Column *"
                                        variant="outlined"
                                        error={Boolean(formik.errors.columnOne)}
                                        helperText={Boolean(formik.touched.columnOne) && formik.errors.columnOne}
                                        inputProps={{
                                            ...params.inputProps
                                        }}
                                    />
                                }
                                required
                                value={formik.values.columnOne}
                            />
                        </FormControl>
                    </div>
                    <div className={classes.operatorColumn}>
                        <FormControl className={formik.errors.operator ? classes.ErrorWrapper : classes.InputWrapper}>
                            <Autocomplete
                                id="operator"
                                name="operator"
                                autoComplete
                                options={operators}
                                getOptionLabel={(option) => option.display}
                                style={{width: 100}}
                                onChange={(event, values) => handleOptionChange(event, values, 'operator')}
                                onBlur={formik.handleBlur}
                                size="small"
                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        label="Operator *"
                                        variant="outlined"
                                        error={Boolean(formik.errors.operator)}
                                        helperText={Boolean(formik.touched.operator) && formik.errors.operator}
                                        inputProps={{
                                            ...params.inputProps
                                        }}
                                    />
                                }
                                required
                                value={formik.values.operator}
                            />
                        </FormControl>
                    </div>
                    <div className={classes.columnTwo}>
                        <FormControl className={formik.errors.columnTwo ? classes.ErrorWrapper : classes.InputWrapper}>
                            <Autocomplete
                                id="columnTwo"
                                name="columnTwo"
                                autoComplete
                                options={column2Fields}
                                getOptionLabel={(option) => option.title}
                                onChange={(event, values) => handleOptionChange(event, values, 'columnTwo')}
                                onBlur={formik.handleBlur}
                                size="small"
                                renderInput={(params) =>
                                    <TextField
                                        {...params}
                                        label="End Column *"
                                        variant="outlined"
                                        error={Boolean(formik.errors.columnTwo)}
                                        helperText={Boolean(formik.touched.columnTwo) && formik.errors.columnTwo}
                                        inputProps={{
                                            ...params.inputProps
                                        }}
                                    />
                                }
                                required
                                value={formik.values.columnTwo}
                            />
                        </FormControl>
                    </div>
                </div>
                <div className={classes.PrecisionField}>
                    <TextField
                        id="precision"
                        name="precision"
                        type="number"
                        variant={'outlined'}
                        size="small"
                        label={'Precision *'}
                        value={formik.values.precision}
                        onChange={formik.handleChange}
                        error={Boolean(formik.errors.precision)}
                        helperText={Boolean(formik.touched.precision) && formik.errors.precision}
                        InputProps={{
                            onBlur:  formik.handleBlur,
                            classes: {
                                root: classes.PrecisionTextField
                            }
                        }}
                    />
                </div>
            </React.Fragment>
        </BobModal>
    );

};

export default FormulaGridColumnModal;
